import styled from '@emotion/styled';
import { NavLink } from 'react-router-dom';
import {
  layout,
  palette,
  radius,
  spacing,
  shadow,
  transition,
} from '../../../style-utils/theme';
import { font } from '../../../style-utils/text';
import { ActiveIndicator } from '../../../motion';

/**
 * THE ACCOUNT AREA'S OWN FURNITURE.
 *
 * /account used to be a single page whose heading was "Change your password" —
 * so the account menu's only entries were Change Password, Devices and Sign
 * Out, and there was no way for anyone to correct their own name, phone or
 * email. Worse, six notification types deep-linked to /account, which meant
 * "somebody accepted your invitation" and "a payment was declined" both opened
 * a password form.
 *
 * This is the shared shell those four concerns now sit inside: one page, four
 * sections, each with its own URL so a notification, an email footer or the
 * account menu can point at the ONE it means.
 */

export const Tabs = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  margin: ${spacing.md} 0 ${spacing.lg};
  padding: 4px;
  background: ${palette.surfaceMuted};
  border-radius: ${radius.md};
`;

export const Tab = styled(NavLink)`
  /* The active pill is TabIndicator, an absolutely positioned child that
     slides between tabs; the tab is its containing block and its own stacking
     context so the pill can sit under the label without leaking out. */
  position: relative;
  isolation: isolate;
  flex: 1 1 auto;
  text-align: center;
  /* 13px of label inside 8px of padding lands at 40px — four short of the floor,
     on the row every account screen is reached through. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${layout.touchTarget};
  box-sizing: border-box;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  font-family: ${font.family};
  font-size: ${font.size.xs}px;
  font-weight: 600;
  color: ${palette.slate};
  text-decoration: none;
  /* The whole control, not just the label — a 40px-tall tab whose focus ring
     hugs four words reads as a stray outline rather than as "you are here". */
  transition: ${transition.colors};

  &:hover {
    color: ${palette.ink};
  }

  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  /* Colour only: the surface and the shadow travel with TabIndicator. */
  &.active {
    color: ${palette.ink};
  }
`;

export const TabLabel = styled.span`
  position: relative;
  z-index: 1;
`;

/* THE PILL MOVES. One element, rendered inside the active NavLink; framer's
   layoutId carries it to the next tab with the indicator spring. */
export const TabIndicator = styled(ActiveIndicator)`
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: ${radius.sm};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};
  pointer-events: none;
`;

export const SectionHeading = styled.h2`
  margin: 0 0 4px;
  font-family: ${font.family};
  font-size: 18px;
  font-weight: 700;
  color: ${palette.ink};
  text-align: left;
`;

export const SectionLead = styled.p`
  margin: 0 0 ${spacing.md};
  font-family: ${font.family};
  font-size: ${font.size.xs}px;
  line-height: 1.5;
  color: ${palette.slate};
  text-align: left;
`;

export const AvatarRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  margin-bottom: ${spacing.lg};
`;

export const AvatarPreview = styled.div<{ src?: string | null }>`
  width: 72px;
  height: 72px;
  flex: 0 0 72px;
  border-radius: 50%;
  background: ${({ src }) =>
    src ? `url(${src}) center/cover no-repeat` : palette.irisTint};
  color: ${palette.irisDark};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${font.family};
  font-size: ${font.size.xxl}px;
  font-weight: 700;
  border: 1px solid ${palette.line};
`;

export const AvatarActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

/** A file input is unstyleable, so the LABEL is the button and the input hides. */
export const FileButton = styled.label`
  display: inline-block;
  padding: ${spacing.xs} ${spacing.md};
  border-radius: ${radius.sm};
  border: 1px solid ${palette.lineStrong};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${font.family};
  font-size: ${font.size.xs}px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: ${palette.lagoon};
    color: ${palette.irisDark};
  }

  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  /* CLIPPED, NOT JUST TRANSPARENT. WebKit paints a file input's own "Choose
     File / No file chosen" text past a 1px box, and with no positioned
     ancestor here the input sat out at the card's edge — the account card
     scrolled 23px sideways on an iPhone SE (measured 2026-09-11). */
  position: relative;

  input {
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
  }
`;

export const QuietButton = styled.button`
  background: none;
  border: 0;
  padding: 0;
  font-family: ${font.family};
  font-size: ${font.size.xxs}px;
  font-weight: 600;
  color: ${palette.slate};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${palette.ink};
  }

  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    color: ${palette.slateLight};
    cursor: default;
    text-decoration: none;
  }
`;

/**
 * The pending-email strip. Deliberately NOT a success tone: a requested address
 * that has not been confirmed is an unfinished action, and colouring it green
 * is what makes somebody stop checking the mailbox they need to open.
 */
export const PendingBanner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${spacing.xs};
  margin-bottom: ${spacing.md};
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${palette.lineStrong};
  border-left: 3px solid ${palette.iris};
  border-radius: ${radius.sm};
  background: ${palette.surfaceMuted};
  font-family: ${font.family};
  font-size: ${font.size.xxs}px;
  line-height: 1.5;
  color: ${palette.inkSoft};
  text-align: left;
`;

export const DevicesWrap = styled.div`
  text-align: left;
`;
